import { alpha, useTheme } from "@mui/material";
import { getScrollbarStyle } from "@dotlegal/dotlegal-ui-components";
import { createSxStyles } from "@common/createSxStyles";

// The light brand blue of the .legal dot. The theme has no token for it.
const brandDotBlue = "#8BBEE8";
const blockBorderRadius = 20;

export const useNavigationMenuSettingsDialogStyles = () => {
    const theme = useTheme();
    const navy = theme.palette.primary.dark;
    const accent = theme.palette.primary.main;
    const mutedText = alpha(navy, 0.65);
    const divider = alpha(navy, 0.1);

    return createSxStyles({
        content: {
            height: "100%",
            display: "flex",
            flexDirection: "column",
        },
        // The grid takes the height left under the description and is allowed to shrink with it, so on a short
        // window the lists scroll instead of pushing the buttons out of view.
        grid: {
            flex: "1 1 auto",
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.45fr 1fr" },
            gridTemplateRows: "minmax(0, 1fr)",
            gap: 3,
        },
        settingsColumn: {
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
        },
        // DotLegalHeader's center only centres within its own width, so the box around it is what centres it in the dialog
        description: {
            alignSelf: "center",
            flexShrink: 0,
        },
        summaryRow: {
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            marginBottom: 2,
        },
        summaryPill: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: theme.customPalette.collapseBackground,
            borderRadius: 999,
            padding: "6px 14px 6px 8px",
            fontSize: theme.typography.pxToRem(13),
            fontWeight: 600,
            color: navy,
        },
        // The same icon and colour DotLegalCheckbox draws for a checked box
        summaryCheck: {
            fontSize: theme.typography.pxToRem(20),
            color: accent,
        },
        list: {
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            ...getScrollbarStyle(),
        },
        itemBlock: {
            flexShrink: 0,
            borderRadius: `${blockBorderRadius}px`,
            padding: "2px 12px",
            backgroundColor: theme.customPalette.collapseBackground,
        },
        previewColumn: {
            display: { xs: "none", md: "block" },
            position: "relative",
            overflow: "hidden",
            minHeight: 0,
            borderRadius: `${blockBorderRadius}px`,
            background: `linear-gradient(135deg, ${navy}, ${accent})`,
        },
        previewContent: {
            position: "relative",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "36px 32px",
        },
        blobTopRight: {
            position: "absolute",
            top: -80,
            right: -90,
            width: 260,
            height: 260,
            backgroundColor: accent,
            opacity: 0.55,
            borderRadius: "62% 38% 55% 45% / 48% 62% 38% 52%",
        },
        blobBottomLeft: {
            position: "absolute",
            bottom: -90,
            left: -70,
            width: 240,
            height: 240,
            backgroundColor: brandDotBlue,
            opacity: 0.25,
            borderRadius: "42% 58% 38% 62% / 55% 45% 55% 45%",
        },
        previewLabel: {
            position: "relative",
            fontSize: theme.typography.pxToRem(12),
            textTransform: "uppercase",
            letterSpacing: ".08em",
            fontWeight: 600,
            color: theme.palette.primary.light,
        },
        previewCard: {
            position: "relative",
            marginTop: 3,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.customPalette.white,
            borderRadius: `${blockBorderRadius}px`,
            padding: "20px 16px",
        },
        previewList: {
            minHeight: 0,
            overflowY: "auto",
            ...getScrollbarStyle(),
        },
        previewLogo: {
            marginBottom: 2,
            paddingLeft: 1,
        },
        previewItem: {
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            paddingY: 0.75,
            paddingLeft: 1,
            fontSize: theme.typography.pxToRem(13),
            fontWeight: 500,
            color: navy,
        },
        previewDot: {
            width: 6,
            height: 6,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: accent,
        },
        previewSubItem: {
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            paddingY: 0.5,
            paddingLeft: "26px",
            fontSize: theme.typography.pxToRem(12),
            color: mutedText,
        },
        previewSubItemDot: {
            width: 6,
            height: 6,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: brandDotBlue,
        },
        subItemList: {
            display: "flex",
            flexDirection: "column",
            marginLeft: "13px",
            paddingLeft: 2,
            borderLeft: `1.5px solid ${divider}`,
        },
    });
};
